import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { useTranslation } from "@/features/utils/i18n";
import { CheckCircleIcon, PuzzlePieceIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../layout";
import { ToolActivationSwitch } from "./components/tool-activation-switch";
import { ToolAgents } from "./components/tool-agents";
import { ToolList } from "./components/tool-list";

/**
 * The company's modules, in the same list as an agent's skills. What this
 * page adds, once there are several agents, is who uses each one.
 */
export function ToolsPage() {
  const { t } = useTranslation("tools");
  const navigate = useNavigate();
  const company = useCurrentClient()?.client?.id;
  const {
    data: available = [],
    isLoading,
    isError,
    refetch,
  } = useAvailableTools();
  const { data: agents = [] } = useAgents();
  const several = agents.length > 1;

  return (
    <PageLayout
      width="medium"
      title={t("tools.title")}
      description={t("tools.description")}
    >
      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : (
        <ToolList
          items={available}
          isLoading={isLoading}
          isOn={(item) => !!item.clientTool?.enabled}
          activeTitle={(count) => t("tools.list.active", { count })}
          inactiveTitle={t("tools.list.available")}
          emptyActive={
            <EmptyState
              icon={PuzzlePieceIcon}
              title={t("tools.empty.noEnabled")}
            />
          }
          emptyInactive={
            <EmptyState
              compact
              icon={CheckCircleIcon}
              title={t("tools.empty.allEnabled")}
            />
          }
          onOpen={(item) =>
            navigate(`/${company}/settings/tools/${item.tool.id}`)
          }
          renderMeta={
            several
              ? (item) => <ToolAgents item={item} agents={agents} />
              : undefined
          }
          renderSwitch={(item) => <ToolActivationSwitch availableTool={item} />}
        />
      )}
    </PageLayout>
  );
}
